// Module registry + data-attribute auto-init.
//
// Each migrated module exports `init(root, config)` and is registered here
// under its module key (== the module's kebab folder name == its `data-ea-module`
// value). global.ts calls `initModules()` on domReady, which scans the DOM for
// `[data-ea-module]` roots, parses their `data-ea-config` JSON, and runs the
// matching init exactly once.

import { init as accountDetailsPopup } from "./account/account-details-popup"
import { init as enrolledAccounts } from "./account/enrolled-accounts"
import { init as resetPassword } from "./account/reset-password"
import { init as consent } from "./consent/consent"
import { init as consentAch } from "./consent/consent-ach"
import { init as consentIlPay } from "./consent/consent-il-pay"
import { init as consentPay } from "./consent/consent-pay"
import { init as consentReprocessing } from "./consent/consent-reprocessing"
import { init as entryForm } from "./entry/entry-form"
import { init as entryFound } from "./entry/entry-found"
import { init as entryIlFound } from "./entry/entry-il-found"
import { init as entryReturning } from "./entry/entry-returning"
import { init as incomeThreshold } from "./savings/income-threshold"
import { init as offerUnavailable } from "./savings/offer-unavailable"
import { init as savingsIntro } from "./savings/savings-intro"
import { init as savingsIntroIl } from "./savings/savings-intro-il"
import { init as findSolarPopup } from "./self-serve/find-solar-popup"
import { init as selfServe } from "./self-serve/self-serve"
import { init as selfServeFindSolarPopup } from "./self-serve/self-serve-find-solar-popup"
import { init as infoForm } from "./shared/info-form"
import { init as liveHelp } from "./shared/live-help"
import { init as progressBar } from "./shared/progress-bar"
import { init as sidebarVideo } from "./shared/sidebar-video"
import { init as thankYouMessage } from "./shared/thank-you-message"
import { init as usage } from "./usage/usage"
import { init as usageBayou } from "./usage/usage-bayou"
import { init as usageBayouAchVerification } from "./usage/usage-bayou-ach-verification"
import { init as usageBayouVerification } from "./usage/usage-bayou-verification"
import { init as usageIlBayou } from "./usage/usage-il-bayou"
import { init as usageLmiBayou } from "./usage/usage-lmi-bayou"
import { init as usageLmiHansen } from "./usage/usage-lmi-hansen"
import { init as usageVerification } from "./usage/usage-verification"

export type ModuleInit = (root: HTMLElement, config: unknown) => void

const registry: Record<string, ModuleInit> = {}

/** Register an initializer under its module key. */
export function register(moduleKey: string, init: ModuleInit): void {
  registry[moduleKey] = init
}

/** Look up a registered module initializer (undefined if none). */
export function getModule(moduleKey: string): ModuleInit | undefined {
  return registry[moduleKey]
}

/**
 * Scan `scope` for `[data-ea-module]` roots that haven't been initialized yet,
 * and run each registered module initializer once. Safe to call multiple times — the
 * `data-ea-initialized` guard prevents double-binding, and multiple
 * `data-ea-module` modules on one page are each handled.
 */
export function initModules(scope: ParentNode = document): void {
  const roots = scope.querySelectorAll<HTMLElement>("[data-ea-module]:not([data-ea-initialized])")
  roots.forEach((root) => {
    const moduleKey = root.getAttribute("data-ea-module")
    if (!moduleKey) return
    const init = registry[moduleKey]
    if (!init) return

    let config: unknown = {}
    const rawConfig = root.getAttribute("data-ea-config")
    if (rawConfig) {
      try {
        config = JSON.parse(rawConfig)
      } catch (error) {
        console.error(`[ea] invalid data-ea-config for "${moduleKey}":`, error)
      }
    }

    root.setAttribute("data-ea-initialized", "true")
    console.log(
      `%c[ea] init%c ${moduleKey}`,
      "background:#16a34a;color:#fff;font-weight:bold;padding:1px 6px;border-radius:3px",
      "color:#16a34a;font-weight:bold",
    )
    try {
      init(root, config)
    } catch (error) {
      console.error(`[ea] init failed for "${moduleKey}":`, error)
    }
  })
}

// ─── Registrations ─────────────────────────────────────────────────────────────
register("entry-il-found", entryIlFound)
register("account-details-popup", accountDetailsPopup)
register("sidebar-video", sidebarVideo)
register("live-help", liveHelp)
register("thank-you-message", thankYouMessage)
register("progress-bar", progressBar)
register("income-threshold", incomeThreshold)
register("offer-unavailable", offerUnavailable)
// ─── Wave 2 ─────────────────────────────────────────────────────────────────────
register("entry-form", entryForm)
register("find-solar-popup", findSolarPopup)
register("enrolled-accounts", enrolledAccounts)
register("info-form", infoForm)
register("savings-intro", savingsIntro)
register("savings-intro-il", savingsIntroIl)
// ─── Wave 3 ─────────────────────────────────────────────────────────────────────
register("reset-password", resetPassword)
register("entry-returning", entryReturning)
register("entry-found", entryFound)
// ─── Wave 4 ─────────────────────────────────────────────────────────────────────
register("usage", usage)
register("usage-verification", usageVerification)
register("usage-il-bayou", usageIlBayou)
register("usage-bayou-verification", usageBayouVerification)
register("usage-bayou-ach-verification", usageBayouAchVerification)
register("usage-lmi-hansen", usageLmiHansen)
register("usage-bayou", usageBayou)
register("usage-lmi-bayou", usageLmiBayou)
// ─── Wave 5 ─────────────────────────────────────────────────────────────────────
register("consent", consent)
register("consent-ach", consentAch)
register("consent-reprocessing", consentReprocessing)
register("consent-pay", consentPay)
register("consent-il-pay", consentIlPay)
// ─── Wave 6 ─────────────────────────────────────────────────────────────────────
register("self-serve-find-solar-popup", selfServeFindSolarPopup)
register("self-serve", selfServe)
